// transition.js: the cuts. Two worlds render into HDR targets A and B, and
// this pass composites them into Post's scene target. Every cut is a pure
// function of t (0..1 across the transition window), so scrubbing back and
// forth is exact.
//   0 FADE     plain crossfade (placeholder -> real world arrivals)
//   1 FLASH    IGNITE -> HOME: push into the newborn star, white-gold flash,
//              match-cut onto the sunrise (B slides from A's focus to its own)
//   2 ZOOM     HOME -> SCALE: the pale blue dot recedes into a radial zoom
//              streak; the galaxy arrives out of the same streak
//   3 LENS     SCALE -> GRAVITY: a point-mass lens forms over the galaxy core,
//              grows to the black hole's size, then its arrival frame takes over
//              through the matching distortion
//   4 HORIZON  GRAVITY -> YOURS: the frame is stretched into the centre and
//              the iris closes to black; a spark, then the brand nebula
//              opens out of the dark from its star
//   5 BACKDROP one world, blurred and dimmed (the offer page)
import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

const VERT = 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }';

const FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
uniform sampler2D tA, tB;
uniform int mode;
uniform float t, aspect, time, amount, reduced;
uniform vec2 fA, fB, texel;

float g2(float x) { return exp(-x * x); } // a Gaussian bump without pow(negative, 2.0), which GLSL leaves undefined
float h12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float inside(vec2 uv) { vec2 e = smoothstep(vec2(0.0), vec2(0.004), uv) * smoothstep(vec2(0.0), vec2(0.004), 1.0 - uv); return e.x * e.y; }
// NaN/Inf guard: a bad pixel in a world is rebuilt from a ring of good
// neighbours, so it can neither punch a hole nor spread through the blurs
bool badf(float v) { return (floatBitsToUint(v) & 0x7f800000u) == 0x7f800000u; }
bool bad3(vec3 c) { return badf(c.r) || badf(c.g) || badf(c.b); }
vec3 safeTex(sampler2D tx, vec2 uv) {
  vec3 c = texture2D(tx, uv).rgb;
  if (!bad3(c)) return c;
  vec3 acc = vec3(0.0); float n = 0.0;
  for (int i = 0; i < 12; i++) {
    float a = float(i) * 0.5236 + 0.3;
    float rr = 7.0 + 5.0 * float(i - (i / 3) * 3);
    vec3 q = texture2D(tx, clamp(uv + vec2(cos(a), sin(a)) * rr * texel, 0.0, 1.0)).rgb;
    if (!bad3(q)) { acc += q; n += 1.0; }
  }
  return n > 0.0 ? acc / n : vec3(0.0);
}
vec3 SA(vec2 uv) { return safeTex(tA, clamp(uv, 0.0, 1.0)); }
vec3 SB(vec2 uv) { return safeTex(tB, clamp(uv, 0.0, 1.0)); }
// sample, with black outside the frame (space)
vec3 SAm(vec2 uv) { return SA(uv) * inside(uv); }
vec3 SBm(vec2 uv) { return SB(uv) * inside(uv); }
vec2 toS(vec2 d) { return d * vec2(aspect, 1.0); }   // uv delta -> screen-height units
vec2 toU(vec2 d) { return d / vec2(aspect, 1.0); }

vec3 zoomBlurA(vec2 uv, vec2 c, float s, float j) {
  vec3 acc = vec3(0.0); float ws = 0.0;
  for (int i = 0; i < 14; i++) {
    float k = (float(i) + j) / 14.0;
    float w = 1.0 - 0.6 * k;
    acc += SAm(c + (uv - c) * (1.0 - s * k)) * w; ws += w;
  }
  return acc / ws;
}
vec3 zoomBlurB(vec2 uv, vec2 c, float s, float j) {
  vec3 acc = vec3(0.0); float ws = 0.0;
  for (int i = 0; i < 14; i++) {
    float k = (float(i) + j) / 14.0;
    float w = 1.0 - 0.6 * k;
    acc += SBm(c + (uv - c) * (1.0 - s * k)) * w; ws += w;
  }
  return acc / ws;
}
// point-mass lens: image position d (screen units) -> source position
vec2 lensMap(vec2 d, float thetaE) {
  float r2 = max(dot(d, d), 1e-6);
  return d - thetaE * thetaE * d / r2;
}

void main() {
  vec2 p = vUv;
  vec3 col;
  float j = h12(gl_FragCoord.xy + fract(time) * 91.0);

  if (mode == 0) {
    col = mix(SA(p), SB(p), t);

  } else if (mode == 1) {
    // FLASH: the outgoing world already flares to white (the nebula's own
    // ending); a short, hot peak hides the swap, and the sunrise comes out
    // of it overexposed, like eyes adjusting after a flash
    float s = smoothstep(0.47, 0.53, t);
    float zA = 1.0 + 0.3 * smoothstep(0.0, 0.5, t) * smoothstep(0.0, 0.5, t);
    vec2 uvA = fA + (p - fA) / zA;
    float k = 1.0 - smoothstep(0.5, 1.0, t);
    float zB = 1.0 + 0.35 * k * k;
    vec2 off = (fA - fB) * k * k;
    vec2 uvB = fB + (p - fB - off) / zB;
    float k2 = 1.0 - smoothstep(0.5, 0.72, t);
    vec3 a = SA(uvA), b = SB(uvB) * (1.0 + 0.3 * k2 * k2 * k2);
    col = mix(a, b, s);
    // BLOOM BURST over the swap: the star's core, glare and spikes swell and the sunrise
    // comes out of them. Only 'amount' (a short pulse in time that the shell fires when the
    // scroll crosses the swap) goes near white: a frame at rest is never a flat white field
    float fd = (t - 0.5) / 0.05;
    float f = exp(-fd * fd);
    // portrait / phones: measure the burst in units of the frame's SHORT side (a narrow frame is not all
    // core glare), step the field back further and cap the time pulse, so the core, the spikes and the
    // sunrise limb stay readable at the peak (desktop, aspect >= 1: unchanged)
    float sq = clamp(aspect, 0.42, 1.0);
    float port = 1.0 - smoothstep(0.75, 1.0, aspect);
    vec2 q = toS(p - mix(fA, fB, s)) / sq;
    float r = length(q);
    vec2 aq = abs(q);
    vec2 dq = abs(vec2(q.x + q.y, q.x - q.y)) * 0.7071;
    float glare = exp(-r * r * 90.0) * 16.0 + exp(-r * 9.0) * 1.5 + exp(-r * 3.0) * 0.16;
    float spikes = exp(-aq.y * 240.0) * exp(-aq.x * 1.8) + exp(-aq.x * 240.0) * exp(-aq.y * 2.6)
                 + 0.3 * (exp(-dq.y * 320.0) * exp(-dq.x * 4.5) + exp(-dq.x * 320.0) * exp(-dq.y * 4.5));
    vec3 hot = vec3(0.80, 0.90, 1.0); // newborn massive star: blue-white
    // at the peak the field steps back, so the core, glare and spikes carry the frame (portrait phones: the
    // nebula's glow and the sunrise's own white start fill the narrow frame)
    col *= 1.0 - mix(0.62, 0.72, port) * f * smoothstep(0.02, 0.16, r);
    col += hot * f * (glare * (1.0 - 0.3 * port) + spikes * 2.6 * (1.0 - 0.6 * reduced));
    // a thin horizontal flare through the star, like a lens would draw
    float fy = q.y * 70.0;
    col += vec3(0.50, 0.80, 1.0) * f * 1.4 * exp(-fy * fy) * exp(-aq.x * 0.7) * (1.0 - reduced);
    // the momentary peak
    float am = amount * (1.0 - 0.45 * port);
    col *= 1.0 + 0.8 * am;
    col += hot * am * (0.2 + 4.5 * exp(-r * 3.0));

  } else if (mode == 2) {
    // ZOOM (pale blue dot -> the Sun): we rush at the dot, the swap hides inside
    // one point of light, and the galaxy opens around the Sun like an iris
    // (magnifying a panorama only makes a grey smear, so B is never blown up)
    float s = smoothstep(0.47, 0.53, t);
    float sA = 0.3 * smoothstep(0.0, 0.55, t);
    float zA = 1.0 - 0.45 * smoothstep(0.0, 0.6, t);
    vec2 uvA = fA + (p - fA) / zA;
    vec3 a = zoomBlurA(uvA, fA, sA * (1.0 - reduced * 0.8), j);
    float kb = 1.0 - smoothstep(0.5, 1.0, t);
    float zB = 1.0 + 0.3 * kb * kb;
    vec2 uvB = fB + (p - fB) / zB;
    vec3 b = zoomBlurB(uvB, fB, 0.12 * kb * (1.0 - reduced * 0.8), j);
    float rB = length(toS(p - fB));
    float iris = pow(smoothstep(0.5, 0.95, t), 1.6) * 2.2 + 0.02;
    b *= smoothstep(iris, iris * 0.45, rB) * mix(0.35, 1.0, smoothstep(0.5, 0.8, t));
    col = mix(a, b, s);
    float r = length(toS(p - mix(fA, fB, s)));
    float bump = g2((t - 0.5) / 0.07);
    col += vec3(0.8, 0.9, 1.0) * bump * (2.6 * exp(-r * r * 2600.0) + 0.22 * exp(-r * r * 160.0));

  } else if (mode == 3) {
    // LENS (galaxy core -> Sgr A*). A point-mass lens forms over the core with a razor shadow edge and a
    // 1-2 px photon ring (no painted glow, no blur) and grows to the black hole's own shadow size (amount =
    // its shadow radius on screen in screen-height units, from its anchors); then a short cross-fade hands
    // over to the black hole world's arrival frame, its ray-traced Einstein ring of stars, untouched
    // (a radial warp of B to fake growth smeared its stars into radial dashes)
    float rsB = amount > 0.0 ? amount : 0.017;
    float s = smoothstep(0.40, 0.50, t);
    vec3 a = vec3(0.0), b = vec3(0.0);
    if (s < 1.0) {
      vec2 c = mix(fA, fB, smoothstep(0.16, 0.42, t));
      vec2 d = toS(p - c);
      float r = length(d);
      float px = texel.y;
      float g = smoothstep(0.06, 0.42, t);
      float rs = rsB * g;
      float thetaE = rs / 0.6;
      float ca = 0.004 * g * (1.0 - reduced);
      a.r = SA(c + toU(lensMap(d, thetaE * (1.0 + ca)))).r;
      a.g = SA(c + toU(lensMap(d, thetaE))).g;
      a.b = SA(c + toU(lensMap(d, thetaE * (1.0 - ca)))).b;
      a *= mix(1.0, smoothstep(rs - 1.2 * px, rs + 1.2 * px, r), smoothstep(0.06, 0.16, t));
      float e = (r - rs - 1.5 * px) / (1.2 * px);
      a += vec3(0.92, 0.90, 1.0) * 0.8 * smoothstep(0.12, 0.4, t) * exp(-e * e);
    }
    if (s > 0.0) b = SB(p);
    col = mix(a, b, s);

  } else if (mode == 4) {
    // HORIZON
    vec2 dA = toS(p - fA);
    float rA = length(dA);
    float ta = smoothstep(0.0, 0.5, t);
    vec2 uvA = fA + (p - fA) * (1.0 - 0.35 * ta * ta);
    float iris = 1.0 - smoothstep(1.6 * (1.0 - ta), 1.6 * (1.0 - ta) + 0.25, rA);
    vec3 a = SA(uvA) * iris * (1.0 - smoothstep(0.35, 0.5, t));
    // v5.1 (LIGHT.md cut 3): the closing iris is edged in violet, then black, then the brand
    a += vec3(0.62, 0.45, 1.0) * 0.55 * g2((rA - 1.6 * (1.0 - ta) - 0.1) / 0.07) * smoothstep(0.02, 0.12, t) * (1.0 - smoothstep(0.3, 0.48, t));
    vec2 dB = toS(p - fB);
    float rB = length(dB);
    float tb = smoothstep(0.55, 1.0, t);
    float zB = 1.0 + 0.35 * (1.0 - tb) * (1.0 - tb);
    vec3 b = SB(fB + (p - fB) / zB);
    float open = 1.0 - smoothstep(1.9 * tb * tb, 1.9 * tb * tb + 0.45, rB);
    b *= open * pow(tb, 1.3);
    float spark = g2((t - 0.56) / 0.05);
    col = a + b + vec3(0.78, 0.84, 1.0) * spark * (6.0 * exp(-rB * rB * 1800.0) + 0.6 * exp(-rB * rB * 40.0));

  } else if (mode == 7) {
    // PULL (the gate): while the visitor holds, space itself flows into the
    // protostar: three phase-shifted zoom layers make a continuous inward
    // drift, with a slight swirl and streaks that grow with the charge
    vec2 d = toS(p - fA);
    float r = length(d);
    float k = amount * (1.0 - 0.7 * reduced);
    float ang = k * 0.55 * exp(-r * r * 16.0); // swirl only near the star: edges never leave the frame
    vec2 dr = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * d;
    vec3 acc = vec3(0.0); float ws = 0.0;
    for (int i = 0; i < 3; i++) {
      float ph = fract(time * (0.18 + 0.5 * k) + float(i) / 3.0);
      float w = 1.0 - abs(ph * 2.0 - 1.0);
      vec2 q = fA + toU(dr * (1.0 + k * 0.16 * ph * (0.4 + 0.6 * exp(-r * 1.5))));
      vec3 c = SA(q);
      vec3 c2 = SA(fA + (q - fA) * (1.0 + 0.05 * k * (0.5 + j)));
      acc += (c * 0.6 + c2 * 0.4) * w; ws += w;
    }
    col = acc / max(ws, 1e-3);
    col *= 1.0 + 0.35 * k * k * exp(-r * r * 30.0);

  } else if (mode == 6) {
    // REBIRTH (a new brand name): the old nebula is pulled into its own star,
    // a spark, and the new one unfolds from the same point
    vec2 dA = toS(p - fA);
    float ta = smoothstep(0.0, 0.46, t);
    float ang = 2.4 * ta * ta * exp(-length(dA) * 2.5) * (1.0 - reduced);
    vec2 rd = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * dA;
    vec2 uvA = fA + toU(rd) * (1.0 + 5.0 * ta * ta * ta);
    vec3 a = SAm(uvA) * (1.0 - smoothstep(0.3, 0.5, t)) * (1.0 + 2.5 * ta * ta);
    vec2 dB = toS(p - fB);
    float tb = smoothstep(0.5, 1.0, t);
    vec3 b = SBm(fB + (p - fB) * (1.0 + 4.0 * pow(1.0 - tb, 3.0))) * smoothstep(0.5, 0.8, t);
    float spark = g2((t - 0.5) / 0.06);
    float rB = length(dB);
    col = a + b + vec3(0.86, 0.84, 1.0) * spark * (7.0 * exp(-rB * rB * 1400.0) + 0.9 * exp(-rB * rB * 30.0));

  } else {
    // BACKDROP: golden-angle disc blur, radius by amount
    float rad = amount * 0.05;
    vec3 acc = SA(p) ; float ws = 1.0;
    if (rad > 0.0005) {
      for (int i = 0; i < 24; i++) {
        float fi = float(i) + j;
        float rr = sqrt(fi / 24.0) * rad;
        float ang = fi * 2.39996;
        acc += SA(p + toU(vec2(cos(ang), sin(ang)) * rr)); ws += 1.0;
      }
    }
    col = acc / ws;
  }
  gl_FragColor = vec4(max(col, 0.0), 1.0);
}`;

export const MODE = { fade: 0, flash: 1, zoom: 2, lens: 3, horizon: 4, backdrop: 5, rebirth: 6, pull: 7 };
// which of the two worlds a cut actually shows at t (the other one need not render)
export function cutNeeds(mode, t) {
  if (mode === MODE.flash) return [t < 0.535, t > 0.465];
  if (mode === MODE.zoom) return [t < 0.625, t > 0.375];
  if (mode === MODE.lens) return [t < 0.51, t > 0.395];
  if (mode === MODE.horizon) return [t < 0.505, t > 0.545];
  return [true, true];
}

export class Compositor {
  constructor(renderer, { half }) {
    this.r = renderer;
    this.type = half ? THREE.HalfFloatType : THREE.UnsignedByteType;
    this.u = {
      tA: { value: null }, tB: { value: null }, mode: { value: 0 }, t: { value: 0 }, aspect: { value: 1 }, time: { value: 0 },
      amount: { value: 0 }, reduced: { value: 0 },
      fA: { value: new THREE.Vector2(0.5, 0.5) }, fB: { value: new THREE.Vector2(0.5, 0.5) }, texel: { value: new THREE.Vector2(1, 1) },
    };
    this.q = new FullScreenQuad(new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: this.u, depthTest: false, depthWrite: false }));
    this.A = null; this.B = null; this.F = null;
    this.w = 1; this.h = 1;
  }

  targets() {
    if (!this.A) {
      const o = { type: this.type, depthBuffer: true, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter };
      this.A = new THREE.WebGLRenderTarget(this.w, this.h, o);
      this.B = new THREE.WebGLRenderTarget(this.w, this.h, o);
    }
    return [this.A, this.B];
  }

  // a third target that holds a frozen frame (the brand rebirth)
  freeze() {
    if (!this.F) this.F = new THREE.WebGLRenderTarget(this.w, this.h, { type: this.type, depthBuffer: true, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    return this.F;
  }

  setSize(w, h) {
    this.w = Math.max(1, w | 0); this.h = Math.max(1, h | 0);
    if (this.A) { this.A.setSize(this.w, this.h); this.B.setSize(this.w, this.h); }
    if (this.F) this.F.setSize(this.w, this.h);
    this.u.aspect.value = this.w / this.h;
    this.u.texel.value.set(1 / this.w, 1 / this.h);
  }

  // composite A (and B) into `target`
  draw(target, mode, t, opt = {}) {
    const u = this.u;
    u.tA.value = opt.texA || this.A.texture;
    u.tB.value = this.B.texture;
    u.mode.value = mode;
    u.t.value = t;
    u.time.value = opt.time || 0;
    u.amount.value = opt.amount || 0;
    u.reduced.value = opt.reduced ? 1 : 0;
    if (opt.fA) u.fA.value.set(opt.fA[0], opt.fA[1]);
    if (opt.fB) u.fB.value.set(opt.fB[0], opt.fB[1]);
    this.r.setRenderTarget(target);
    this.q.render(this.r);
  }

  // compile the program up front (no hitch at the first cut)
  warm() {
    const [A] = this.targets();
    const tmp = new THREE.WebGLRenderTarget(4, 4, { type: this.type });
    this.u.tA.value = A.texture; this.u.tB.value = A.texture;
    this.r.setRenderTarget(tmp);
    this.q.render(this.r);
    this.r.setRenderTarget(null);
    tmp.dispose();
  }

  dispose() {
    this.q.material.dispose(); this.q.dispose();
    if (this.A) { this.A.dispose(); this.B.dispose(); }
    if (this.F) this.F.dispose();
  }
}
